import { filter, findIndex } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'
import type { GetProductDetailResponse } from '~/types/apis/v2/getProductDetail'

export interface ProductDetails {
  id: string
  artwork_product_id: string
  product_variant_detail_id: string
  sku: string
  slug: string
  name: string
  artwork_name?: string
  open_for_sale: number
  device_name: string
  product_image?: string
  case_color?: string
  device_color?: string
  is_hidden?: boolean
  is_available?: boolean
  is_sold_out?: boolean
  is_exclusive?: boolean
  is_ready_to_order?: boolean
  artwork_slug?: string
  cart_item_id?: string
  artwork_open_for_sale?: number
  artwork_product_open_for_sale?: number
  product_open_for_sale?: number
  open_for_sale_message_en?: string
  open_for_sale_message_th?: string
  product_open_for_sale_message_en?: string
  product_open_for_sale_message_th?: string
  artwork_open_for_sale_message_en?: string
  artwork_open_for_sale_message_th?: string
  artwork_product_open_for_sale_message_en?: string
  artwork_product_open_for_sale_message_th?: string
  shared_url?: string
  type?: string
  status?: string
}

export type CartStateV2 = {
  items: {
    cartItemId: string
    productSlug: string
    productId: string
    artworkSlug?: string
    productSku: string
    artworkProductId?: string
    quantity: number
    metadata?: {
      backgroundUrl?: string | null
      artworkUrl?: string | null
      textStyle: { id: string; name: string; url: string }
      textPosition: string
      textPositionId: string
      textColors: { id: string; colors: string; rotation: number }
      personalizeText: {
        raw: string
      }
    }
    personalization?: {
      thumbnail_base64?: string
      shared_url?: string
      background_color: {
        hex: string
        image: string
        cmyk: {
          c: string
          m: string
          y: string
          k: string
        }
      }
      stickers: {
        id: string
        x: number
        y: number
        width: number
        height: number
        image: string
        rotation: number
      }[]
      texts: {
        id: string
        x: number
        y: number
        width?: number
        height?: number
        text: string
        rotation: number
        color: string
        font_type: string
        font_size: number
        custom_color: string
        position_type: string
      }[]
      photos: {
        id: string
        x: number
        y: number
        width: number
        height: number
        type: string
        url: string
        aspectRatio: number
        rotation: number
      }[]
      device: {
        label: string
        value: string
        slug: string
        category: string
        category_slug: string
        device_image: string
        case_image: string
        mark_image: string
        color: string
      }
      case: {
        slug: string
        name: string
        product_variant_detail_id: string
      }
      case_color: { value: string; label: string }
    }
    personalization_base64?: string
    matchLevel?: string
    details?: ProductDetails
  }[]
  itemsData: {
    productSlug?: string // เปลี่ยนจาก optional เป็น required
    cartItemId: string
    open_for_sale?: string
    variantName: string
    productName: string
    artworkName?: string
    productSku: string
    artworkSku?: string
    price: number
    thumbnailUrl: string | null
    productVariable?: GetProductDetailResponse['data']['product_variable']
    productOptionProduct?: GetProductDetailResponse['data']['product_option_product']
  }[]
}
export const useCartStoreV2 = defineStore('cartStoreV2', {
  state: (): CartStateV2 => ({ items: [], itemsData: [] }),
  actions: {
    addToCart(item: Omit<CartStateV2['items'][number], 'cartItemId'>) {
      const cartItemId = uuidv4()
      const productSlug = item.productSlug
      const artworkSlug = item.artworkSlug

      const existingItem = findIndex(this.items, {
        productSlug: productSlug,
        artworkSlug: artworkSlug
      })

      if (
        existingItem !== -1 &&
        !this.items[existingItem].metadata &&
        !item.personalization
      ) {
        this.items[existingItem].quantity += item.quantity
      } else {
        this.items.push({ ...item, cartItemId })
      }
      return cartItemId
    },
    handleUpdateBase64(index: number, base64: string) {
      this.items[index].personalization_base64 = base64
    },
    handleUpdateItem(cartItemId: string, data: CartStateV2['items'][number]) {
      const itemIndex = findIndex(this.items, { cartItemId })
      if (this.items[itemIndex] && itemIndex !== -1) {
        this.items[itemIndex] = data
      }
    },
    handleDecreaseQuantity(cartItemId: string) {
      const itemIndex = findIndex(this.items, { cartItemId })
      if (
        this.items[itemIndex] &&
        this.items[itemIndex].quantity &&
        this.items[itemIndex]?.quantity > 1
      ) {
        this.items[itemIndex].quantity--
      } else {
        this.items.splice(itemIndex, 1)
        this.itemsData = filter(
          this.itemsData,
          (item) => item.cartItemId !== cartItemId
        )
      }
    },
    handleIncreaseQuantity(cartItemId: string) {
      const itemIndex = findIndex(this.items, { cartItemId })
      if (this.items[itemIndex]) {
        this.items[itemIndex].quantity++
      }
    },
    removeFromCart(cartItemId: string) {
      const itemIndex = findIndex(this.items, { cartItemId })
      if (this.items[itemIndex]) {
        this.items.splice(itemIndex, 1)
        this.itemsData = filter(
          this.itemsData,
          (item) => item.cartItemId !== cartItemId
        )
      }
    },
    handleSetQuantity(cartItemId: string, quantity: number) {
      const itemIndex = findIndex(this.items, { cartItemId })
      if (this.items[itemIndex]) {
        this.items[itemIndex].quantity = quantity
      }
    },
    updateProductData(product: {
      cartItemId: string
      variantName: string
      productName: string
      artworkName?: string
      productSku: string
      artworkSku?: string
      price: number
      thumbnailUrl: string | null
    }) {
      const newItemsData = [...this.itemsData]
      const currentDataIndex = findIndex(this.itemsData, {
        cartItemId: product.cartItemId
      })

      const cartItem = this.items.find(
        (item) => item.cartItemId === product.cartItemId
      )
      const productWithSlug = {
        ...product,
        productSlug: cartItem?.productSlug || 'unknown',
        open_for_sale: cartItem?.details?.open_for_sale
      }

      if (currentDataIndex !== -1) {
        newItemsData[currentDataIndex] = {
          ...productWithSlug,
          open_for_sale: String(productWithSlug.open_for_sale)
        }
      } else {
        newItemsData.push({
          ...productWithSlug,
          open_for_sale: String(productWithSlug.open_for_sale)
        })
      }

      this.itemsData = newItemsData
    },
    clearCart() {
      this.items = []
      this.itemsData = []
    },
    setState(cartState: CartStateV2) {
      this.items = cartState.items
      this.itemsData = cartState.itemsData
    }
  },

  persist: {
    storage: persistedState.localStorage
  }
})
